import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import useToken from "../../../customHooks/useToken";
import APIArticleService from "../../../services/BlogService";
import { mapArticleSearchResults } from "../../../constants/parse/articles";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getUserIdFromState } from "../../../mappers/backendMappers";

const NewsContext = createContext();

const NewsProvider = (props) => {
  let history = useHistory();

  const { children } = props;
  // Pagination removed: render full list
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState([]);
  const [allArticlesData, setAllArticlesData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const csrfToken = useToken();
  const userId = useSelector(getUserIdFromState);
  const roles = useSelector(({ applicationUser }) => applicationUser?.roles);

  // Pagination removed: no page handling or URL sync

  // Fetch articles when search term or category changes
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all articles (using a large itemsPerPage to get all)
        const result = await APIArticleService.searchArticles(
          searchTerm,
          category.map((r) => r.value),
          0, // page (ignored for full list on client)
          100000, // very large page size to fetch effectively all
          userId || "00000000-0000-0000-0000-000000000000",
          csrfToken
        );

        setAllArticlesData(result);
        // Set categories from the articles data
        if (result && result.categories) {
          setCategories(result.categories);
        }
      } catch (err) {
        console.error("Error fetching articles:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [searchTerm, category, userId, csrfToken]);

  // No page resets needed

  // Keep smooth scroll on navigation
  useEffect(() => {
    return history.listen(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [history]);

  // No initial page to set

  // Build full, reverse-chronological list
  const fullArticles = useMemo(() => {
    if (!allArticlesData) {
      return { data: [], meta: { count: 0 } };
    }

    const mappedData = mapArticleSearchResults(allArticlesData);
    const items = (mappedData?.data || []).slice();

    items.sort((a, b) => {
      const da = a.created ? new Date(a.created).getTime() : 0;
      const db = b.created ? new Date(b.created).getTime() : 0;
      return db - da; // newest first
    });

    return {
      data: items,
      meta: { count: items.length },
    };
  }, [allArticlesData]);

  const value = {
    categories,
    articles: fullArticles,
    searchTerm,
    category,
    loading,
    error,
    onSearch: setSearchTerm,
    onCategoryChange: setCategory,
  };

  return <NewsContext.Provider value={value}>{children}</NewsContext.Provider>;
};

const useNews = () => {
  const context = useContext(NewsContext);
  if (context === undefined) {
    throw new Error("useNews must be used within a NewsProvider");
  }
  return context;
};

export { NewsProvider, useNews };
